import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpenCheck,
  CheckCircle2,
  GraduationCap,
  HandHeart,
  HeartPulse,
  Monitor,
  ShieldCheck,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useRegionalExperience } from "../hooks/useRegionalExperience";
import PageBanner from "../components/PageBanner";

const icons = {
  HeartPulse,
  MonitorCog: Monitor,
  Monitor,
  GraduationCap,
  ShieldPlus: ShieldCheck,
  ShieldCheck,
  HandHeart,
  BookOpenCheck,
};

const Programs = () => {
  const { t } = useTranslation();
  const { siteConfig, region } = useRegionalExperience();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="overflow-x-hidden bg-[#fffaf5] text-[#14201f]">
      <PageBanner {...(siteConfig.programsPage?.banner || {})} />

      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10">
            {siteConfig.programPillars.map((program, index) => {
              const Icon = icons[program.icon] || ShieldCheck;
              const reverse = index % 2 === 1;
              return (
                <article
                  key={program.id}
                  className="grid overflow-hidden rounded-lg border border-[#dfe8e6] bg-white shadow-sm lg:grid-cols-2"
                >
                  <div
                    className={`aspect-[16/11] overflow-hidden lg:aspect-auto ${reverse ? "lg:order-2" : ""}`}
                  >
                    <picture>
                      {program.imageMobile ? (
                        <source srcSet={program.imageMobile} media="(max-width: 767px)" />
                      ) : null}
                      <img
                        src={program.image}
                        alt={program.title}
                        className="h-full min-h-[360px] w-full object-cover"
                        loading="lazy"
                      />
                    </picture>
                  </div>
                  <div className="flex flex-col justify-center p-7 md:p-10">
                    <div className="flex items-center gap-4">
                      <span className="flex h-12 w-12 items-center justify-center rounded-md bg-[#f8efe5] text-[#9d1f1c]">
                        <Icon size={24} />
                      </span>
                      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9d1f1c]">
                        {program.eyebrow}
                      </p>
                    </div>
                    <h2 className="mt-6 text-3xl font-semibold leading-tight text-[#14201f] md:text-4xl">
                      {program.title}
                    </h2>
                    <p className="mt-5 text-base leading-8 text-[#52615f]">{program.description}</p>
                    <div className="mt-7 grid gap-3">
                      {program.metrics.map((metric) => (
                        <div key={metric} className="flex gap-3 text-sm leading-6 text-[#33413f]">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#9d1f1c]" />
                          <span>{metric}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                      <Link
                        to={`/programs/${program.id}`}
                        className="inline-flex items-center justify-center gap-3 rounded-md bg-[#9d1f1c] px-5 py-3 text-sm font-bold uppercase tracking-[0.12em] text-white transition hover:bg-[#7f1916]"
                      >
                        {t("home.programs.exploreProgram")}
                        <ArrowRight size={18} />
                      </Link>
                      <Link
                        to={program.ctaHref?.startsWith("#") ? "/vedant" : program.ctaHref}
                        className="inline-flex items-center justify-center gap-3 rounded-md border border-[#dfe8e6] px-5 py-3 text-sm font-bold uppercase tracking-[0.12em] text-[#14201f] transition hover:border-[#9d1f1c] hover:text-[#9d1f1c]"
                      >
                        {program.ctaLabel}
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[linear-gradient(135deg,#2a120d_0%,#4b2418_54%,#7f1916_100%)] py-20 text-white md:py-28">
        <div className="container mx-auto grid gap-10 px-4 sm:px-6 lg:grid-cols-[1fr_0.9fr] lg:px-8">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#f5c45e]">
              {t("programs.howGrow.eyebrow")}
            </p>
            <h2 className="text-3xl font-semibold leading-tight md:text-5xl">
              {t("programs.howGrow.heading")}
            </h2>
          </div>
          <div className="grid gap-4">
            {[
              t("programs.howGrow.items.0"),
              t("programs.howGrow.items.1"),
              t("programs.howGrow.items.2"),
              t("programs.howGrow.items.3"),
            ].map((item) => (
              <div
                key={item}
                className="flex gap-3 border-t border-white/15 pt-4 text-sm leading-7 text-white/78"
              >
                <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#f5c45e]" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-lg border border-[#dfe8e6] bg-white p-8 text-center shadow-sm">
            <h2 className="text-3xl font-semibold text-[#14201f]">{t("programs.support.title")}</h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-[#52615f]">
              {t("programs.support.description", {
                regionLabel: region.label,
                paymentLabel: region.paymentLabel,
              })}
            </p>
            <Link
              to={region.donationPath}
              className="mt-7 inline-flex items-center gap-3 rounded-md bg-[#f5c45e] px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-[#201711] transition hover:bg-[#ffd77c]"
            >
              {region.paymentLabel}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Programs;
